import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Display only - no database storage
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    }, 5000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      details: 'support@Blackmesamer.com',
      link: 'mailto:support@Blackmesamer.com'
    },
    {
      icon: Phone,
      title: 'Phone',
      details: '+1 574 566 7535',
      link: 'tel:+15745667535'
    },
    {
      icon: MapPin,
      title: 'Address',
      details: '1109 Cincinnati St, Lafayette, IN 47904',
      link: 'https://maps.google.com/?q=1109+Cincinnati+St,+Lafayette,+IN+47904'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Mon - Fri: 9AM - 6PM EST',
      link: null
    }
  ];

  const services = [
    'Print on Demand',
    'Dropshipping Fulfillment',
    'Product Sourcing',
    'Global Warehousing',
    'Custom Branding',
    'TikTok Shop Solutions',
    'Other / Custom Solution'
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
              Contact Us
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Chivo'] font-bold text-gray-900 mb-6" data-testid="contact-hero-title">
              Let's Start{' '}
              <span className="text-green-600">Working Together</span>
            </h1>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Ready to scale your ecommerce business? Get in touch with our team to 
              discuss how we can help with your fulfillment needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-6">
                Get in Touch
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Have questions about our services? Want to discuss a custom solution? 
                Our team is here to help you succeed.
              </p>

              <div className="space-y-6 mb-10">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <info.icon size={22} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-gray-600 hover:text-green-600 transition-colors"
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-gray-600">{info.details}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Map Placeholder */}
              <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100 relative">
                <img
                  src="https://images.pexels.com/photos/4386321/pexels-photo-4386321.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Location"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-3"
            >
              <div className="bg-gray-50 rounded-3xl p-8 md:p-10">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="text-2xl font-['Chivo'] font-bold text-gray-900 mb-3">
                      Message Sent!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for reaching out. Our team will get back to you within 24 hours.
                    </p>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 text-green-600 font-semibold hover:gap-3 transition-all"
                    >
                      Back to Home
                      <ArrowRight size={18} />
                    </Link>
                  </motion.div>
                ) : (
                  <>
                    <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-2">
                      Send Us a Message
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Fill out the form below and we'll get back to you shortly.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name" className="text-gray-700 mb-2 block">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            data-testid="input-name"
                            className="w-full rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-gray-700 mb-2 block">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            data-testid="input-email"
                            className="w-full rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone" className="text-gray-700 mb-2 block">
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+1 (555) 000-0000"
                            data-testid="input-phone"
                            className="w-full rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                          />
                        </div>
                        <div>
                          <Label htmlFor="company" className="text-gray-700 mb-2 block">
                            Company Name
                          </Label>
                          <Input
                            id="company"
                            name="company"
                            type="text"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder="Your Company"
                            data-testid="input-company"
                            className="w-full rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="service" className="text-gray-700 mb-2 block">
                          Service Interested In
                        </Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          data-testid="select-service"
                          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 bg-white text-gray-900 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none transition-colors"
                        >
                          <option value="">Select a service</option>
                          {services.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-gray-700 mb-2 block">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your business and fulfillment needs..."
                          data-testid="input-message"
                          className="w-full rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20 resize-none"
                        />
                      </div>

                      <Button
                        type="submit"
                        data-testid="submit-button"
                        className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-3 font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        <Send size={18} className="mr-2" />
                        Send Message
                      </Button>

                      <p className="text-gray-500 text-sm text-center">
                        By submitting this form, you agree to be contacted by our team.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-3">
              Explore Our Services
            </h2>
            <p className="text-gray-600">
              Learn more about how we can help your business grow.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Print on Demand', description: 'Custom printing without inventory', link: '/services#print-on-demand' },
              { title: 'Dropshipping', description: 'Seamless order fulfillment', link: '/services#dropshipping' },
              { title: 'Global Warehousing', description: 'Strategic storage worldwide', link: '/services#global-warehousing' },
              { title: 'Product Sourcing', description: 'Find quality products', link: '/services#product-sourcing' },
              { title: 'Custom Branding', description: 'Create memorable experiences', link: '/services#custom-branding' },
              { title: 'TikTok Shop', description: 'Social commerce fulfillment', link: '/services#tiktok-shop' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <Link
                  to={item.link}
                  data-testid={`service-link-${index}`}
                  className="block bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 group border border-gray-100 hover:border-green-200 hover:-translate-y-1"
                >
                  <h3 className="font-['Chivo'] font-bold text-gray-900 mb-1 group-hover:text-green-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
